import Color from "../language/Color.js"

/**
 * @description
 *	The accent used when the integration has no branding, or has branding
 *	without an accent color. It is the end of the Sanii gradient, the only one
 *	of the three that reaches AA against white text.
 */
const DEFAULT_ACCENT_COLOR = "#6f2e90"

/**
 * @description
 *	The 2 (two) candidates to paint over the accent color. The one with the
 *	highest contrast ratio against the accent wins.
 */
const LIGHT_ON_ACCENT_COLOR = "#ffffff"
const DARK_ON_ACCENT_COLOR = "#111111"

/**
 * @description
 *	The alpha channel of the soft variant of the accent, written as the last
 *	byte of an 8 (eight) digit hexadecimal color: 0x1f over 0xff is 12.16%.
 */
const SOFT_ACCENT_ALPHA = "1f"

/**
 * @description
 *	The set of colors derived from the accent color of an integration.
 */
export type AccentPalette = Readonly<{
	accent: string
	accentSoft: string
	onAccent: string
}>

/**
 * @description
 *	Derives the palette of an integration from its accent color, falling back
 *	to the accent of Sanii when the integration has none.
 *
 * @remarks
 *	The foreground is chosen by contrast ratio instead of by luminance
 *	threshold, with the very same 2 (two) candidates that the backend checks
 *	on write: an accent over which neither of them reaches the contrast of a
 *	text is rejected there, so the winner here is always legible.
 */
const AccentPalette = (accentColor: string | undefined): AccentPalette => {
	const accent = accentColor ?? DEFAULT_ACCENT_COLOR
	const onAccent =
		Color.contrastRatio(accent, DARK_ON_ACCENT_COLOR) <= Color.contrastRatio(accent, LIGHT_ON_ACCENT_COLOR)
			? LIGHT_ON_ACCENT_COLOR
			: DARK_ON_ACCENT_COLOR
	return { accent, accentSoft: `${accent}${SOFT_ACCENT_ALPHA}`, onAccent }
}

export default AccentPalette
